import {
  ContactType,
  ExternalTaxRatesInterface,
  Region,
  TaxRateInterface,
  TaxRuleInterface,
} from 'client/Interfaces/ExternalVATRatesInterface'
import { pickBy } from 'lodash'

import { areWithinFrenchTerritories, countriesWithinEU } from '../Countries'
import { l } from '../Translation'

const DEFAULT_PREDEFINED_TAG = 's_fr_200'

const externalTaxRateRegions: Region[] = ['local', 'eu', 'world']

export interface VatPctMap {
  [id: string]: string
}

// get tax group for contact's/customer's country based on locale
export const getRegionFromCountry = (country?: string | null, userLocale?: string): Region | undefined => {
  if (!country) {
    return
  }

  if (userLocale?.includes(country)) {
    return 'local'
  }

  if (userLocale && areWithinFrenchTerritories(userLocale.split('_')[1], country)) {
    return 'local'
  }

  if (countriesWithinEU.includes(country)) {
    return 'eu'
  }

  return 'world'
}

const getAllowedVatRateIdsByRegion = (externalVATRates: ExternalTaxRatesInterface, region?: Region) => {
  // if no region is provided, return all tax rate ids
  if (!region) {
    return externalVATRates.rules.map((rule) => rule.taxRateId)
  }

  return externalVATRates.rules?.filter((rule) => rule.region === region)?.map((rule) => rule.taxRateId)
}

export const cleanTaxRatesForRegion = (
  externalVATRates: ExternalTaxRatesInterface,
  region?: Region,
): TaxRateInterface[] => {
  if (!externalVATRates || !externalVATRates.taxRates || !externalVATRates.rules) {
    return []
  }

  const allowedVatRateIds = getAllowedVatRateIdsByRegion(externalVATRates, region)

  return externalVATRates.taxRates
    ?.filter((taxRate) => {
      if (taxRate.id && taxRate.name && (!region || allowedVatRateIds.includes(taxRate.id))) {
        return taxRate
      }
    })
    ?.sort((a, b) => a?.taxRateGroup?.localeCompare(b.taxRateGroup))
}

const getDefaultTaxRate = (taxRates: TaxRateInterface[]) => {
  return taxRates.find((taxRate) => taxRate?.predefinedTag === DEFAULT_PREDEFINED_TAG) || taxRates?.[0]
}

export const getTaxRateByVatRateId = (
  externalVATRates: ExternalTaxRatesInterface,
  vatRateId?: string,
  customerCountry?: string | null,
  userLocale?: string,
) => {
  const region = getRegionFromCountry(customerCountry, userLocale)
  const taxRatesForGroup = cleanTaxRatesForRegion(externalVATRates, region)
  const originTaxRate = taxRatesForGroup.find((taxRate) => taxRate?.id === vatRateId)
  const taxRate = originTaxRate || getDefaultTaxRate(taxRatesForGroup)
  return {
    id: taxRate?.id,
    value: taxRate?.rate,
  }
}

export const getTaxRateByVatPct = (
  externalVATRates: ExternalTaxRatesInterface,
  vatPct?: number,
  customerCountry?: string | null,
  userLocale?: string,
) => {
  const region = getRegionFromCountry(customerCountry, userLocale)
  const taxRatesForGroup = cleanTaxRatesForRegion(externalVATRates, region)
  const originTaxRate = taxRatesForGroup.find(
    (taxRate) => Number(taxRate?.rate).toFixed(4) === Number(vatPct).toFixed(4) && taxRate.taxRateGroup === 'local',
  )
  const taxRate = originTaxRate || getDefaultTaxRate(taxRatesForGroup)
  return {
    id: taxRate?.id,
    value: taxRate?.rate,
  }
}

const constructVatRatesMap = (taxRates: TaxRateInterface[], title: string, key: string): VatPctMap => {
  const initMap = { [key]: title }
  return taxRates.reduce((map, taxRate) => {
    return { ...map, [taxRate.id]: taxRate.name }
  }, initMap)
}

export const getVatRatesMapByRegion = (
  externalVATRates: ExternalTaxRatesInterface,
  region?: Region,
  locale?: string,
): VatPctMap => {
  const vatRates = cleanTaxRatesForRegion(externalVATRates, region)
  const key = `title-${region}`
  const tOrganizationCountry = l(`taxRate.title.${locale ? locale.split('_')[1] : region}`)
  switch (region) {
    case 'local':
      return constructVatRatesMap(vatRates, tOrganizationCountry, key)

    case 'eu':
      return constructVatRatesMap(vatRates, l('taxRate.title.europe'), key)

    case 'world':
      return constructVatRatesMap(vatRates, l('taxRate.title.world'), key)

    case undefined:
    default:
      // construct map for tax group and aggregate each group
      return externalTaxRateRegions.reduce((init, region) => {
        const VATRatesForRegion = cleanTaxRatesForRegion(externalVATRates, region)
        const VATRatesMap =
          VATRatesForRegion?.length > 0
            ? constructVatRatesMap(VATRatesForRegion, l(`taxRate.title.${region}`), `title-${region}`)
            : {}
        return { ...init, ...VATRatesMap }
      }, {})
  }
}

export const getAllowedVatRatesMap = (
  externalVATRates: ExternalTaxRatesInterface,
  customerCountry?: string | null,
  locale?: string,
  productType?: string,
  customerType?: ContactType | null,
): VatPctMap => {
  const region = getRegionFromCountry(customerCountry, locale)
  let vatPctMap = getVatRatesMapByRegion(externalVATRates, region, locale)
  if (productType || customerType) {
    const allowedTaxRateIds = getAllowedVatRateIdsByProductTypeAndCustomerTypeAndRegion(
      externalVATRates?.rules,
      productType,
      customerType,
      region,
    )

    if (allowedTaxRateIds) {
      vatPctMap = pickBy(vatPctMap, (_value, key: string) => {
        return allowedTaxRateIds.includes(key) || key.startsWith('title')
      })
    }
  }

  return vatPctMap
}

export const getAllowedVatRateIdsByProductTypeAndCustomerTypeAndRegion = (
  rules: TaxRuleInterface[],
  productType: string | undefined,
  customerType?: ContactType | null,
  region?: Region,
) => {
  if (rules) {
    const allowedTaxRateIds = rules
      .filter((rule) => {
        if (region && rule.region !== region) {
          return false
        }

        let productTypeMatch = true
        let customerTypeMatch = true

        if (productType) {
          productTypeMatch = rule.productType === productType.toLowerCase() || rule.productType === 'any'
        }

        if (customerType) {
          customerTypeMatch = rule.contactType === customerType || rule.contactType === 'any'
        }

        return productTypeMatch && customerTypeMatch
      })
      .map((rule) => rule.taxRateId)
    return allowedTaxRateIds
  }
}
